export const TRACKER_VERSION = 1;
export const TRACKER_CHANNEL_COUNT = 4;
export const TRACKER_ROWS_PER_PATTERN = 64;
export const TRACKER_NOTES_PER_OCTAVE = 12;
export const TRACKER_NOTE_LOWEST_OCTAVE = 2;
export const TRACKER_NOTE_HIGHEST_OCTAVE = 7;
export const TRACKER_NOTE_COUNT =
  (TRACKER_NOTE_HIGHEST_OCTAVE - TRACKER_NOTE_LOWEST_OCTAVE + 1) * TRACKER_NOTES_PER_OCTAVE;
export const TRACKER_NOTE_EMPTY = 0x00;
export const TRACKER_NOTE_OFF = 0xfe;
export const TRACKER_NOTE_CUT = 0xff;
export const TRACKER_INSTRUMENT_EMPTY = 0xff;
export const TRACKER_MAX_COMPILED_INSTRUMENTS = 16;
export const TRACKER_MAX_COMPILED_PATTERNS = 4;
export const TRACKER_MAX_ORDERS = 64;
export const TRACKER_DEFAULT_SPEED = 6;
export const TRACKER_DEFAULT_TEMPO = 125;

export const TRACKER_PITCH_MODE = {
  coarse16: 0,
  balanced19: 1,
  fine20: 2
} as const;

export const TRACKER_WAVEFORM = {
  triangle: "triangle",
  saw: "saw",
  pulse: "pulse",
  noise: "noise"
} as const;

export const TRACKER_EFFECT = {
  none: 0x00,
  off: 0x01,
  arp: 0x02,
  por: 0x03,
  vib: 0x04,
  pwm: 0x05,
  ret: 0x06,
  spd: 0x10,
  brk: 0x11,
  jmp: 0x12,
  fct: 0x13,
  frs: 0x14,
  mvl: 0x15
} as const;

export type TrackerPitchMode = (typeof TRACKER_PITCH_MODE)[keyof typeof TRACKER_PITCH_MODE];
export type TrackerWaveform = (typeof TRACKER_WAVEFORM)[keyof typeof TRACKER_WAVEFORM];
export type TrackerEffectCode = (typeof TRACKER_EFFECT)[keyof typeof TRACKER_EFFECT];
export type TrackerEffectScope = "none" | "voice" | "song";

export type ChannelEvent = {
  note: number;
  instrument: number;
  effect: TrackerEffectCode;
  effectArg: number;
};

export type TrackerPattern = {
  name: string;
  rows: ChannelEvent[][];
};

export type TrackerInstrument = {
  name: string;
  waveform: TrackerWaveform;
  sync: boolean;
  ring: boolean;
  test: boolean;
  ad: number;
  sr: number;
  pulseWidth: number;
  auxRateX8: boolean;
  filterRouteEnabled: boolean;
  color: string;
  favorite: boolean;
};

export type TrackerGlobals = {
  pitchMode: TrackerPitchMode;
  masterVolume: number;
  filterCutoff: number;
  filterCtrl: number;
};

export type TrackerSong = {
  speed: number;
  tempo: number;
  rowsPerPattern: number;
  orderList: number[];
  patterns: TrackerPattern[];
  instruments: TrackerInstrument[];
  globals: TrackerGlobals;
};

export type TrackerProject = {
  version: number;
  title: string;
  song: TrackerSong;
};

export function isTrackerControlNote(note: number): boolean {
  return note === TRACKER_NOTE_OFF || note === TRACKER_NOTE_CUT;
}

export function isTrackerPitchNote(note: number): boolean {
  return Number.isInteger(note) && note >= 1 && note <= TRACKER_NOTE_COUNT;
}

const TRACKER_EFFECT_VALUES = new Set<number>(Object.values(TRACKER_EFFECT));

const DEFAULT_COLORS = ["#f2b84b", "#63d4c8", "#ff8a5b", "#8db6ff"];

function clampByte(value: number): number {
  return Math.max(0, Math.min(0xff, Math.round(value)));
}

function clampNibble(value: number): number {
  return Math.max(0, Math.min(0x0f, Math.round(value)));
}

function clampPulseWidth(value: number): number {
  return Math.max(0, Math.min(0x0fff, Math.round(value)));
}

function clampFilterCutoff(value: number): number {
  return Math.max(0, Math.min(0x07ff, Math.round(value)));
}

function clampMasterVolume(value: number): number {
  return Math.max(0, Math.min(0x0f, Math.round(value)));
}

function defaultEvent(): ChannelEvent {
  return {
    note: TRACKER_NOTE_EMPTY,
    instrument: TRACKER_INSTRUMENT_EMPTY,
    effect: TRACKER_EFFECT.none,
    effectArg: 0x00
  };
}

export function cloneEvent(event: ChannelEvent): ChannelEvent {
  return { ...event };
}

export function createEmptyPattern(name = "Pattern 00"): TrackerPattern {
  return {
    name,
    rows: Array.from({ length: TRACKER_ROWS_PER_PATTERN }, () =>
      Array.from({ length: TRACKER_CHANNEL_COUNT }, () => defaultEvent())
    )
  };
}

export function createDefaultInstruments(): TrackerInstrument[] {
  return [
    {
      name: "Saw Lead",
      waveform: TRACKER_WAVEFORM.saw,
      sync: false,
      ring: false,
      test: false,
      ad: 0x24,
      sr: 0xc4,
      pulseWidth: 0x0800,
      auxRateX8: false,
      filterRouteEnabled: false,
      color: DEFAULT_COLORS[0],
      favorite: true
    },
    {
      name: "Pulse Bass",
      waveform: TRACKER_WAVEFORM.pulse,
      sync: false,
      ring: false,
      test: false,
      ad: 0x12,
      sr: 0x84,
      pulseWidth: 0x0600,
      auxRateX8: false,
      filterRouteEnabled: true,
      color: DEFAULT_COLORS[1],
      favorite: true
    },
    {
      name: "Tri Bell",
      waveform: TRACKER_WAVEFORM.triangle,
      sync: false,
      ring: false,
      test: false,
      ad: 0x18,
      sr: 0xb6,
      pulseWidth: 0x0800,
      auxRateX8: true,
      filterRouteEnabled: false,
      color: DEFAULT_COLORS[2],
      favorite: false
    },
    {
      name: "Noise Hat",
      waveform: TRACKER_WAVEFORM.noise,
      sync: false,
      ring: false,
      test: false,
      ad: 0x01,
      sr: 0x23,
      pulseWidth: 0x0800,
      auxRateX8: false,
      filterRouteEnabled: false,
      color: DEFAULT_COLORS[3],
      favorite: false
    }
  ];
}

export function createInstrumentTemplate(index: number): TrackerInstrument {
  return sanitizeInstrument(undefined, index);
}

export function createDefaultProject(): TrackerProject {
  return {
    version: TRACKER_VERSION,
    title: "Untitled Tracker Song",
    song: {
      speed: TRACKER_DEFAULT_SPEED,
      tempo: TRACKER_DEFAULT_TEMPO,
      rowsPerPattern: TRACKER_ROWS_PER_PATTERN,
      orderList: [0],
      patterns: [createEmptyPattern()],
      instruments: createDefaultInstruments(),
      globals: {
        pitchMode: TRACKER_PITCH_MODE.balanced19,
        masterVolume: 0x0f,
        filterCutoff: 0x07ff,
        filterCtrl: 0x00
      }
    }
  };
}

export function structuredCloneProject(project: TrackerProject): TrackerProject {
  return structuredClone(project);
}

function sanitizeEvent(raw: Partial<ChannelEvent> | undefined): ChannelEvent {
  const note = raw?.note ?? TRACKER_NOTE_EMPTY;
  if (
    !Number.isInteger(note)
    || (note !== TRACKER_NOTE_EMPTY && !isTrackerControlNote(note) && !isTrackerPitchNote(note))
  ) {
    throw new Error(`tracker note ${String(note)} is outside the supported C-2..B-7/OFF/CUT range`);
  }
  const instrument = raw?.instrument ?? TRACKER_INSTRUMENT_EMPTY;
  if (!Number.isInteger(instrument) || instrument < 0 || instrument > 0xff) {
    throw new Error(`tracker instrument reference ${String(instrument)} is invalid`);
  }
  const effect = raw?.effect ?? TRACKER_EFFECT.none;
  if (!Number.isInteger(effect) || !TRACKER_EFFECT_VALUES.has(effect)) {
    throw new Error(`tracker effect opcode ${String(effect)} is unknown`);
  }
  const effectArg = raw?.effectArg ?? 0x00;
  if (!Number.isInteger(effectArg) || effectArg < 0 || effectArg > 0xff) {
    throw new Error(`tracker effect argument ${String(effectArg)} exceeds the v1 8-bit range`);
  }
  return {
    note,
    instrument,
    effect: effect as TrackerEffectCode,
    effectArg
  };
}

function sanitizePattern(raw: Partial<TrackerPattern> | undefined, index: number): TrackerPattern {
  const name = typeof raw?.name === "string" && raw.name.trim() !== "" ? raw.name.trim() : `Pattern ${index.toString().padStart(2, "0")}`;
  const rows: ChannelEvent[][] = [];
  for (let rowIndex = 0; rowIndex < TRACKER_ROWS_PER_PATTERN; rowIndex += 1) {
    const sourceRow = raw?.rows?.[rowIndex];
    rows.push(
      Array.from({ length: TRACKER_CHANNEL_COUNT }, (_, channelIndex) => sanitizeEvent(sourceRow?.[channelIndex]))
    );
  }
  return { name, rows };
}

function sanitizeInstrument(raw: Partial<TrackerInstrument> | undefined, index: number): TrackerInstrument {
  const waveform =
    raw?.waveform === TRACKER_WAVEFORM.triangle ||
    raw?.waveform === TRACKER_WAVEFORM.saw ||
    raw?.waveform === TRACKER_WAVEFORM.pulse ||
    raw?.waveform === TRACKER_WAVEFORM.noise
      ? raw.waveform
      : TRACKER_WAVEFORM.saw;
  return {
    name: typeof raw?.name === "string" && raw.name.trim() !== "" ? raw.name.trim() : `Instrument ${(index + 1).toString().padStart(2, "0")}`,
    waveform,
    sync: raw?.sync === true,
    ring: raw?.ring === true,
    test: raw?.test === true,
    ad: clampByte(raw?.ad ?? 0x24),
    sr: clampByte(raw?.sr ?? 0xc4),
    pulseWidth: clampPulseWidth(raw?.pulseWidth ?? 0x0800),
    auxRateX8: raw?.auxRateX8 === true,
    filterRouteEnabled: raw?.filterRouteEnabled === true,
    color: typeof raw?.color === "string" && raw.color.trim() !== "" ? raw.color : DEFAULT_COLORS[index % DEFAULT_COLORS.length],
    favorite: raw?.favorite === true
  };
}

export function sanitizeProject(raw: unknown): TrackerProject {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("tracker project must be an object");
  }
  const candidate = raw as Partial<TrackerProject>;
  if (candidate.version !== TRACKER_VERSION) {
    throw new Error(`unsupported tracker project version ${String(candidate.version)}`);
  }
  const song = candidate.song;
  if (typeof song !== "object" || song === null) {
    throw new Error("tracker project is missing song data");
  }

  if (!Array.isArray(song.patterns) || song.patterns.length === 0) {
    throw new Error("tracker project must contain at least one pattern");
  }
  if (!Array.isArray(song.instruments) || song.instruments.length === 0) {
    throw new Error("tracker project must contain at least one instrument");
  }
  if (!Array.isArray(song.orderList) || song.orderList.length === 0) {
    throw new Error("tracker project must contain at least one order entry");
  }

  const patternsSource = song.patterns;
  const instrumentsSource = song.instruments;
  const orderListSource = song.orderList;

  const patterns = patternsSource.map((pattern, index) => sanitizePattern(pattern, index));
  const instruments = instrumentsSource.map((instrument, index) => sanitizeInstrument(instrument, index));
  const orderList = orderListSource.map((value) => {
    const numeric = Number(value);
    if (!Number.isInteger(numeric) || numeric < 0 || numeric >= patterns.length) {
      throw new Error(`tracker order references missing pattern ${String(value)}`);
    }
    return numeric;
  });

  for (const pattern of patterns) {
    for (const row of pattern.rows) {
      for (const event of row) {
        if (event.instrument !== TRACKER_INSTRUMENT_EMPTY && event.instrument >= instruments.length) {
          throw new Error(`tracker pattern references missing instrument ${event.instrument + 1}`);
        }
      }
    }
  }

  return {
    version: TRACKER_VERSION,
    title: typeof candidate.title === "string" && candidate.title.trim() !== "" ? candidate.title.trim() : "Untitled Tracker Song",
    song: {
      speed: Math.max(1, Math.min(31, Math.round(Number(song.speed ?? TRACKER_DEFAULT_SPEED)))),
      tempo: Math.max(32, Math.min(255, Math.round(Number(song.tempo ?? TRACKER_DEFAULT_TEMPO)))),
      rowsPerPattern: TRACKER_ROWS_PER_PATTERN,
      orderList,
      patterns,
      instruments,
      globals: {
        pitchMode:
          song.globals?.pitchMode === TRACKER_PITCH_MODE.coarse16 ||
          song.globals?.pitchMode === TRACKER_PITCH_MODE.fine20
            ? song.globals.pitchMode
            : TRACKER_PITCH_MODE.balanced19,
        masterVolume: clampMasterVolume(Number(song.globals?.masterVolume ?? 0x0f)),
        filterCutoff: clampFilterCutoff(Number(song.globals?.filterCutoff ?? 0x07ff)),
        filterCtrl: clampByte(Number(song.globals?.filterCtrl ?? 0x00))
      }
    }
  };
}

export function instrumentCtrlValue(instrument: TrackerInstrument): number {
  let value = 0;
  if (instrument.sync) {
    value |= 0x02;
  }
  if (instrument.ring) {
    value |= 0x04;
  }
  if (instrument.test) {
    value |= 0x08;
  }
  switch (instrument.waveform) {
    case TRACKER_WAVEFORM.triangle:
      value |= 0x10;
      break;
    case TRACKER_WAVEFORM.saw:
      value |= 0x20;
      break;
    case TRACKER_WAVEFORM.pulse:
      value |= 0x40;
      break;
    case TRACKER_WAVEFORM.noise:
      value |= 0x80;
      break;
  }
  return value;
}

export function effectName(effect: TrackerEffectCode): string {
  switch (effect) {
    case TRACKER_EFFECT.off:
      return "CUT";
    case TRACKER_EFFECT.arp:
      return "ARP";
    case TRACKER_EFFECT.por:
      return "POR";
    case TRACKER_EFFECT.vib:
      return "VIB";
    case TRACKER_EFFECT.pwm:
      return "PWM";
    case TRACKER_EFFECT.ret:
      return "RET";
    case TRACKER_EFFECT.spd:
      return "SPD";
    case TRACKER_EFFECT.brk:
      return "BRK";
    case TRACKER_EFFECT.jmp:
      return "JMP";
    case TRACKER_EFFECT.fct:
      return "FCT";
    case TRACKER_EFFECT.frs:
      return "FRS";
    case TRACKER_EFFECT.mvl:
      return "MVL";
    default:
      return "---";
  }
}

export function isValidEffectCode(value: number): value is TrackerEffectCode {
  return Number.isInteger(value) && TRACKER_EFFECT_VALUES.has(value);
}

export function effectHelp(effect: TrackerEffectCode): string {
  switch (effect) {
    case TRACKER_EFFECT.off:
      return "Immediately silences the current voice. Use note-column OFF when you want release to run.";
    case TRACKER_EFFECT.arp:
      return "Cycles base, high-nibble, and low-nibble semitone offsets on later ticks.";
    case TRACKER_EFFECT.por:
      return "Slides the current voice toward the new note in semitone steps.";
    case TRACKER_EFFECT.vib:
      return "Applies a simple pitch wobble around the current note.";
    case TRACKER_EFFECT.pwm:
      return "Moves the pulse width by the signed effect byte each tick.";
    case TRACKER_EFFECT.ret:
      return "Restarts the gate every N ticks when the effect byte is non-zero.";
    case TRACKER_EFFECT.spd:
      return "Changes tracker speed in ticks per row for following rows.";
    case TRACKER_EFFECT.brk:
      return "Breaks to the next order when the row completes.";
    case TRACKER_EFFECT.jmp:
      return "Jumps to the given order index after the row completes.";
    case TRACKER_EFFECT.fct:
      return "Sets the shared filter cutoff from the effect byte.";
    case TRACKER_EFFECT.frs:
      return "Sets the shared filter resonance and mode byte.";
    case TRACKER_EFFECT.mvl:
      return "Sets the master volume low nibble.";
    default:
      return "No effect command.";
  }
}

export function effectScope(effect: TrackerEffectCode): TrackerEffectScope {
  switch (effect) {
    case TRACKER_EFFECT.off:
    case TRACKER_EFFECT.arp:
    case TRACKER_EFFECT.por:
    case TRACKER_EFFECT.vib:
    case TRACKER_EFFECT.pwm:
    case TRACKER_EFFECT.ret:
      return "voice";
    case TRACKER_EFFECT.spd:
    case TRACKER_EFFECT.brk:
    case TRACKER_EFFECT.jmp:
    case TRACKER_EFFECT.fct:
    case TRACKER_EFFECT.frs:
    case TRACKER_EFFECT.mvl:
      return "song";
    default:
      return "none";
  }
}

export function effectScopeLabel(effect: TrackerEffectCode): string {
  switch (effectScope(effect)) {
    case "voice":
      return "Voice";
    case "song":
      return "Song";
    default:
      return "No Effect";
  }
}

export function effectArgHint(effect: TrackerEffectCode): string {
  switch (effect) {
    case TRACKER_EFFECT.none:
      return "No argument is used while the effect is empty.";
    case TRACKER_EFFECT.off:
      return "Usually left at 00 because CUT silences the current voice immediately. Use note OFF for gate release.";
    case TRACKER_EFFECT.arp:
      return "High nibble = first semitone offset, low nibble = second semitone offset. Example: 37.";
    case TRACKER_EFFECT.por:
      return "8-bit slide rate toward the current note. Smaller values glide more gently.";
    case TRACKER_EFFECT.vib:
      return "Compact vibrato byte. Start with small values such as 11 or 21 for subtle motion.";
    case TRACKER_EFFECT.pwm:
      return "Signed pulse-width step applied every tick. Example: F8 narrows, 08 widens.";
    case TRACKER_EFFECT.ret:
      return "Gate restart interval in ticks. 00 disables retriggering.";
    case TRACKER_EFFECT.spd:
      return "New tracker speed in ticks per row for following rows. Example: 06.";
    case TRACKER_EFFECT.brk:
      return "Usually 00. Break moves playback to the next order after this row ends.";
    case TRACKER_EFFECT.jmp:
      return "Order index to jump to after this row. Example: 02 jumps to order slot 02.";
    case TRACKER_EFFECT.fct:
      return "8-bit tracker cutoff value that expands into the shared AUD_FC filter range.";
    case TRACKER_EFFECT.frs:
      return "Packed resonance and filter-mode byte for the shared filter.";
    case TRACKER_EFFECT.mvl:
      return "Low nibble sets master volume. Example: 0C keeps headroom below 0F.";
    default:
      return "Effect argument uses a compact 8-bit tracker byte.";
  }
}

export function sanitizeOrderList(values: number[], patternCount: number): number[] {
  const filtered = values
    .map((value) => Math.max(0, Math.min(patternCount - 1, Math.round(value))))
    .filter((value) => Number.isFinite(value));
  return filtered.length === 0 ? [0] : filtered.slice(0, TRACKER_MAX_ORDERS);
}

export function sanitizeInstrumentIndex(value: number, instrumentCount: number): number {
  if (instrumentCount <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(instrumentCount - 1, Math.round(value)));
}

export function sanitizePatternIndex(value: number, patternCount: number): number {
  if (patternCount <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(patternCount - 1, Math.round(value)));
}

export function sanitizeEffectArg(value: number): number {
  return clampByte(value);
}

export function sanitizeInstrumentByte(value: number): number {
  return value === TRACKER_INSTRUMENT_EMPTY ? TRACKER_INSTRUMENT_EMPTY : clampNibble(value);
}
